import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';

const process = [
  {
    number: '01',
    title: 'Frame',
    description: 'Get specific about the customer, the constraint, and what winning looks like.',
  },
  {
    number: '02',
    title: 'Make',
    description:
      'Prototype the riskiest assumptions and turn the strongest direction into software.',
  },
  {
    number: '03',
    title: 'Learn',
    description: 'Put the product in front of real people, instrument it, and respond to evidence.',
  },
  {
    number: '04',
    title: 'Scale',
    description: 'Strengthen the system, team, and operating model as traction grows.',
  },
];

const principles = [
  'Work with the people doing the work',
  'Make decisions visible and reversible',
  'Prototype before over-investing',
  'Leave the team stronger than we found it',
];

const About = () => (
  <section id="approach" className="border-y border-line/10 bg-panel-alt py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-8">
      <div className="grid gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="section-kicker">How Consltr works</p>
          <h2 className="magazine-title mt-4 text-5xl tracking-[-0.045em] sm:text-7xl">
            Small team.
            <span className="block text-faint">Full ownership.</span>
          </h2>
          <p className="mt-8 max-w-xl text-lg leading-8 text-muted">
            Consltr is a consulting company for work that does not fit neatly into a slide deck or a
            development ticket. We connect business context, customer experience, and technical
            execution so the whole product moves together.
          </p>
          <a
            href="#contact"
            data-agent-id="agent://consltr/approach/start-conversation"
            data-semantic-intent="navigate"
            className="mt-8 inline-flex items-center gap-2 border-b border-accent/50 pb-1 text-sm font-semibold text-accent transition-colors hover:border-accent"
          >
            Tell us what is stuck
            <ArrowUpRight size={16} aria-hidden="true" />
          </a>
        </motion.div>

        <div>
          <div className="grid gap-px overflow-hidden rounded-[1.75rem] border border-line/10 bg-line/10 sm:grid-cols-2">
            {process.map((step, index) => (
              <motion.article
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.07 }}
                className="process-card bg-panel p-7 sm:p-8"
              >
                <span className="font-mono text-xs text-accent">{step.number}</span>
                <h3 className="magazine-title mt-10 text-3xl">{step.title}</h3>
                <p className="mt-3 leading-7 text-faint">{step.description}</p>
              </motion.article>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-5 rounded-[1.75rem] border border-line/10 p-7 sm:p-8"
          >
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-faint">
              Working principles
            </p>
            <ul className="mt-6 grid gap-4 sm:grid-cols-2">
              {principles.map(principle => (
                <li key={principle} className="flex gap-3 text-sm leading-6 text-muted">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-highlight" />
                  {principle}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);

export default About;
